import React, { useState, useMemo } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";
import FilePreviewModal from "./FilePreviewModal";
import DocUploadModal from "./DocUploadModal";
import "./style.css";

export default function TableDeltahouse({
  transactions,
  onTransactionUpdated,
}) {
  const filteredTransactions = transactions.filter(
    (transaction) => transaction.company === "Deltahouse" && transaction
  );
  const [editingId, setEditingId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDocUpload, setShowDocUplad] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [transID, setTransID] = useState("");

  const [editForm, setEditForm] = useState({
    date: "",
    type: "",
    company: "",
    category: "",
    description: "",
    amount: "",
    document: "",
  });

  const openPreview = async (pathFromDb) => {
    const res = await api.get(`/files/${pathFromDb}`, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    setPreviewUrl(blobUrl);
    setShowPreview(true);
  };

  const onDocUpload = async (transID) => {
    setTransID(transID);
    setShowDocUplad(true);
  };

  // ✅ Compute Totals using useMemo for performance
  const totals = useMemo(() => {
    const income = filteredTransactions
      .filter((t) => Number(t.amount) > 0)
      .reduce((sum, t) => sum + Number(t.amount), 0);

    const expense = filteredTransactions
      .filter((t) => Number(t.amount) < 0)
      .reduce((sum, t) => sum + Number(t.amount), 0);

    const balance = income + expense;
    return { income, expense, balance };
  }, [filteredTransactions]);

  const startEdit = (transaction) => {
    setEditingId(transaction.id);
    setEditForm({
      date: transaction.date.split("T")[0], // for <input type="date" />
      type: transaction.type,
      company: transaction.company,
      category: transaction.category,
      description: transaction.description,
      amount: transaction.amount,
      document: transaction.document,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      date: "",
      type: "",
      company: "",
      category: "",
      description: "",
      amount: "",
    });
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const saveEdit = async (id) => {
    const finalAmount =
      editForm.type === "expense"
        ? -Math.abs(parseFloat(editForm.amount))
        : Math.abs(parseFloat(editForm.amount));
    try {
      await api.put(`/transactions/${id}`, {
        ...editForm,
        amount: finalAmount,
      });
      toast.success("Transaction updated successfully");
      setEditingId(null);
      if (onTransactionUpdated) onTransactionUpdated();
    } catch (err) {
      toast.error("Failed to update transaction");
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this transaction? This action cannot be undone."
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Transaction deleted successfully");
      if (onTransactionUpdated) onTransactionUpdated();
    } catch (err) {
      toast.error("Failed to delete transaction");
    }
  };

  return (
    <>
      <div className="mt-3">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Company</th>
              <th className="mob">Description</th>
              <th className="mob">Category</th>
              <th>Amount</th>
              <th>Document</th>
              <th className="mob">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((t) => (
              <tr key={t.id}>
                {editingId === t.id ? (
                  <>
                    {/* Editable Row */}
                    <td>
                      <input
                        type="date"
                        name="date"
                        className="form-control"
                        value={editForm.date}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <select
                        name="type"
                        className="form-select"
                        value={editForm.type}
                        onChange={handleEditChange}
                      >
                        <option value="income">Income</option>
                        <option value="expense">Expense</option>
                      </select>
                    </td>
                    <td>
                      <select
                        id="company"
                        name="company"
                        className="form-select"
                        value={editForm.company}
                        onChange={handleEditChange}
                      >
                        <option value="None">Select</option>
                        <option value="Castinvestment">Castinvestment</option>
                        <option value="Kerminue">Kerminue</option>
                        <option value="Deltahouse">Deltahouse</option>
                        <option value="Abaskhiron">Abaskhiron</option>
                        <option value="Neuroninvest">Neuroninvest</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        name="description"
                        className="form-control"
                        value={editForm.description}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="category"
                        className="form-control"
                        value={editForm.category}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        name="amount"
                        className="form-control"
                        value={editForm.amount}
                        onChange={handleEditChange}
                      />
                    </td>
                    <button
                      onClick={() => {
                        onDocUpload(t.id);
                      }}
                    >
                      Update Document
                    </button>
                    <td>
                      <button
                        className="btn btn-sm btn-success me-2"
                        onClick={() => saveEdit(t.id)}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={cancelEdit}
                      >
                        Cancel
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    {/* Normal Row */}
                    <td>{new Date(t.date).toLocaleDateString("en-AU")}</td>
                    <td>{t.type}</td>
                    <td>{t.company}</td>
                    <td className="mob">{t.description}</td>
                    <td className="mob">{t.category}</td>
                    <td
                      className={t.amount < 0 ? "text-danger" : "text-success"}
                    >
                      {t.amount}
                    </td>
                    <td>
                      {t.document ? (
                        <button
                          onClick={() => {
                            openPreview(t.document.split("/")[1]);
                          }}
                        >
                          View Document
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            onDocUpload(t.id);
                          }}
                        >
                          Upload Document
                        </button>
                      )}
                    </td>
                    <td className="mob">
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => startEdit(t)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(t.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}

            {/* Totals Row */}
            <tr className="fw-bold">
              <td colSpan="3" className="text-center mob-td2">
                Totals:
              </td>
              <td colSpan="4" className="mob-td">
                <span className="text-success me-3">
                  Income: {totals.income.toFixed(2)}
                </span>
                <span className="text-danger me-3">
                  Expense: {totals.expense.toFixed(2)}
                </span>
                <span
                  className={
                    totals.balance >= 0 ? "text-success" : "text-danger"
                  }
                >
                  Balance: {totals.balance.toFixed(2)}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <FilePreviewModal
          show={showPreview}
          onHide={() => setShowPreview(false)}
          fileUrl={previewUrl}
          title="Document"
        />
        <DocUploadModal
          show={showDocUpload}
          onHide={() => setShowDocUplad(false)}
          transID={transID}
          onUpload={onTransactionUpdated}
        />
      </div>
    </>
  );
}
